<template>
    <div class="barchart__wrapper">
        <svg class="barchart__svg" v-show="hasData" viewbox="0 0 755 400" :width="vw < 755 ? 375 : 755" :height="vw < 755 ? 300 : 400" ref="barchart"></svg>
        <div class="barchart__svg barchart__empty" v-if="!hasData">
            Ingen registreringer funnet for {{ selectedYear }}.
        </div>
    </div>
</template>
<script>
    import { ref } from 'vue';
    import * as d3 from "d3";
    import { getIconStyle } from './items.js';

    export default {
        props: [
            'activeGroup',
            'userRegistrations',
            'selectedYear',
        ],
        setup() {
            const svg      = null;
            const barchart = ref(null);
            const vw       = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);

            return {
                svg,
                barchart,
                vw
            };
        },
        computed: {
            activeGroupRegistrations() {
                const group = (this.userRegistrations || []).find(i => i.id === Number(this.activeGroup));

                return group ? group.registrations : [];
            },
            hasData() {
                return this.activeGroupRegistrations.length > 0;
            },
        },
        methods: {
            makeDiagram() {
                this.barchart.innerHTML = '';

                if (!this.hasData) {
                    return;
                }

                this.svg = d3.select(this.barchart);
                const yMargin = 125,
                      xMargin = this.vw < 755 ? 50 : 100,
                      width   = this.svg.attr("width") - xMargin,
                      height  = this.svg.attr("height") - yMargin;
    
                const items = [];
                const group   = this.userRegistrations.filter(i => {
                    return i.id === Number(this.activeGroup);
                })[0];

                for (const registration of group.registrations) {
                    const filtered = items.filter(i => {
                        return i.id === registration.type_id;
                    });

                    if (filtered.length === 0) {
                        items.push({
                            id: registration.type_id,
                            name: registration.type_name,
                            amount: registration.amount,
                            uom: registration.uom.name,
                            illustration_src: registration.illustration_src,
                        });
                    } else {
                        filtered[0].amount += registration.amount;
                    }
                }

                // Set min / max values for X and Y
                const xScale = d3.scaleBand().range([0, width]).padding(0.99),
                    yScale = d3.scaleLinear().range([height, 0]);
                
                // Add initial group in SVG and position according to margins
                const g = this.svg.append("g")
                .attr("transform", "translate(" + xMargin/2 + "," + yMargin/2 + ")");
        
                // Set the domains of x and y based on the data
                xScale.domain(items.map((d) => d.name));
                yScale.domain([0, d3.max(items, (d) => d.amount) + 3]);
        
                // Define bar width and amount of lines on the Y axis
                const barWidth = xScale.bandwidth(),
                      ySteps   = yScale.ticks();
        
                // Draw axes
                this._drawXAxis(g, xScale, height);
                this._drawYAxis(g, yScale);
        
        
                // Draw horizontal grid
                g.selectAll("line.horizontalGrid").data(ySteps).enter().append("line")
                .attr("class", "barchart__grid")
                .attr("x1", 0)
                .attr("x2", width)
                .attr("y1", (d) => yScale(d))
                .attr("y2", (d) => yScale(d));
                //
        
        
                // Draw bars based on data
                g.selectAll("bar").data(items).enter().append("rect")
                .attr("class", (d) => 'barchart__bar barchart__bar--' + d.id)
                .attr("x", (d) => xScale(d.name))
                .attr("y", (d) => yScale(d.amount))
                .attr("width", 1.2)
                .attr("height", (d) => height - yScale(d.amount));
                // 
        
        
                // Draw berry icons on top of bars
                g.selectAll("items").data(items).enter().append("svg:image")
                .attr("href", (d) => d.illustration_src)
                .attr("class", (d) => 'barchart__icon')
                .attr("x", (d) => xScale(d.name) - 32)
                .attr("y", (d) => yScale(d.amount) - 30);
                //
        
                // Draw amounts in liters above the berry images
                g.selectAll("items").data(items).enter().append("text")
                .text((d) => this.hasDecimal(d.amount) ? d.amount.toFixed(1) + ' ' + d.uom : d.amount + ' ' + d.uom)
                .attr("x", (d) => xScale(d.name) - 16)
                .attr("y", (d) => yScale(d.amount) - 40);
                // 
            },
            _drawXAxis(g, xScale, height) {        
                g.append("g")
                .attr("transform", "translate(0," + height + ")")
                .call(d3.axisBottom(xScale));
            },
            _drawYAxis(g, yScale) {        
                g.append("g")
                .call(d3.axisLeft(yScale).tickFormat((d) => d).ticks(10));
            },
            hasDecimal: function(num) {
	            return !!(num % 1);
            },
        },
        watch: {
            userRegistrations: function(newVal, oldVal) {
                if (newVal) {
                    this.makeDiagram();
                }
            },
            activeGroup: function(newVal, oldVal) {
                if (newVal && this.userRegistrations && this.barchart) {
                    this.makeDiagram();
                }
            }
        },
    }
</script>